import {
  ALL_FEATURES,
  CONTROL_DEPENDENT_FEATURES,
  FEATURE,
  FEATURE_OPTIONS,
  normalizeFeatures,
  withControlDependency,
  type Feature,
} from '../features';

/** 제어가 꺼져 있어 고를 수 없는 칸의 안내(클립보드·파일은 입력 주입을 쓴다). */
const NEEDS_CONTROL_HINT = '원격 제어가 있어야 쓸 수 있습니다(붙여넣기·놓기를 원격 입력으로 합니다).';

/**
 * 세부 기능 권한 체크 목록(§14.19.2) — 사용자 추가 폼·편집 패널이 함께 쓴다.
 *
 * 클립보드·파일 전송은 **원격 제어가 전제**다 — 제어를 끄면 함께 꺼지고 잠긴다(서버·에이전트와 같은 규칙).
 * 그래서 화면에 보이는 체크 = 실제로 저장되는 값 = 제어 기기에서 쓸 수 있는 것이 된다.
 *
 * @param value 선택된 기능(순서 무관 — 바꿀 때 정해진 순서로 정리해 돌려준다)
 * @param onChange 새 목록(정해진 순서, 제어 의존 규칙 적용)
 * @param disabled 관리자 행처럼 편집 대상이 아닐 때 — 전부 체크된 채로 잠근다(`lockedNote` 로 이유를 보인다)
 * @param lockedNote 잠갔을 때 옆에 보일 한 줄
 */
export function FeatureChecks({
  value,
  onChange,
  disabled = false,
  lockedNote,
}: {
  value: readonly string[];
  onChange: (next: Feature[]) => void;
  disabled?: boolean;
  lockedNote?: string;
}) {
  const shown = disabled ? [...ALL_FEATURES] : withControlDependency(normalizeFeatures(value));
  const controlOn = shown.includes(FEATURE.CONTROL);
  const toggle = (f: Feature, on: boolean) => {
    const set = new Set<string>(shown);
    if (on) set.add(f);
    else set.delete(f);
    onChange(withControlDependency(normalizeFeatures([...set])));
  };
  return (
    <div className="feature-checks" role="group" aria-label="허용 기능">
      {FEATURE_OPTIONS.map((o) => {
        const needsControl = !controlOn && CONTROL_DEPENDENT_FEATURES.includes(o.value);
        return (
          <label
            key={o.value}
            className="feature-check"
            title={needsControl ? NEEDS_CONTROL_HINT : o.hint}
            style={needsControl ? { opacity: 0.5 } : undefined}
          >
            <input
              type="checkbox"
              checked={shown.includes(o.value)}
              disabled={disabled || needsControl}
              onChange={(e) => toggle(o.value, e.target.checked)}
            />
            {o.label}
          </label>
        );
      })}
      {!disabled && (
        <span className="feature-quick">
          <button type="button" className="linklike" onClick={() => onChange([...ALL_FEATURES])}>
            전부
          </button>
          <button type="button" className="linklike" onClick={() => onChange([])}>
            없음
          </button>
        </span>
      )}
      {disabled && lockedNote && <span className="hint">{lockedNote}</span>}
    </div>
  );
}
